'use client';

import type { SideBarItemProps } from 'src/utils/advisor-dash-nav/side-bar';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { clientSectionPath } from './get-active-tab';
import { householdLandingTabPath } from './household-landing-tab';

// Deliberate input, not scroll position: a wheel or a key is the advisor reading, a pointer down is
// the advisor acting. Any of them means the page is no longer ours to move.
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchmove'] as const;

function queryStringOf(asPath: string): string {
  const index = asPath.search(/[?#]/);
  return index === -1 ? '' : asPath.slice(index);
}

type UseHouseholdLandingTabArgs = {
  /** The revamp gate. False leaves the legacy landing — always Overview — exactly as it is. */
  readonly enabled: boolean;
  /**
   * False while the saved tab order, or the client query the nav row is built from, is still in
   * flight for *this* household. Landing is decided once, so deciding early would send the advisor
   * past a tab the client query would have enabled.
   */
  readonly ready: boolean;
  /** The rendered nav row, after `applyTabOrder` and `sortedActiveItems`. */
  readonly navItems: readonly SideBarItemProps[];
  readonly householdPublicId: string;
};

/**
 * Sends the advisor from a household's base route to the first tab in their own order, once per
 * household.
 *
 * Once, because Overview *is* the base route: a second pass would bounce an advisor who
 * deliberately clicked Overview straight back off it. `router.replace` rather than `push` for the
 * same reason — a pushed Overview entry turns Back into the same bounce.
 */
export function useHouseholdLandingTab({
  enabled,
  ready,
  navItems,
  householdPublicId,
}: UseHouseholdLandingTabArgs): void {
  const router = useRouter();
  const [decidedFor, setDecidedFor] = useState<string | null>(null);
  const interactedRef = useRef(false);

  const pending = decidedFor !== householdPublicId;

  // Armed regardless of `enabled`, because `enabled` reads LaunchDarkly through `useFlags()`, which
  // is false until the SDK initializes — the very window an advisor is most likely to start reading
  // in. Gating on it would leave that window unwatched and then discard it on the reset below.
  // `householdPublicId` is load-bearing in the deps: with nothing yet decided, `pending` stays true
  // across a household switch, so it is the only signal that the latch needs clearing.
  useEffect(() => {
    if (!pending) return;
    interactedRef.current = false;
    function latch() {
      interactedRef.current = true;
    }
    for (const type of INTERACTION_EVENTS) {
      document.addEventListener(type, latch, { capture: true, passive: true });
    }
    return () => {
      for (const type of INTERACTION_EVENTS) {
        document.removeEventListener(type, latch, { capture: true });
      }
    };
  }, [pending, householdPublicId]);

  useEffect(() => {
    if (!enabled || !ready || !pending) return;

    const section = clientSectionPath(router.asPath);
    if (section === null) return;

    // Client-side hops between households re-render with an empty row before the new household's
    // queries land. Deciding off that would spend the one-shot and skip landing for the household
    // the advisor actually opened.
    if (navItems.length === 0) return;

    setDecidedFor(householdPublicId);

    // The advisor already moved, or moved the page: honour a deep link, and never yank a page
    // someone has started using. The listeners only exist from the moment this hook mounts, so
    // input in the first ~500ms of a cold load is invisible to them — acceptable because the
    // Overview page is still a spinner there, so there is nothing on screen to be reading.
    if (section !== '' || interactedRef.current) return;

    const target = householdLandingTabPath(navItems);
    if (target === null) return;

    // Carries the query through so the `?revamp=1` preview hatch survives the hop.
    void router.replace(`${target}${queryStringOf(router.asPath)}`);
  }, [enabled, householdPublicId, navItems, pending, ready, router]);
}
